import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateConference, UpdateConference } from "~pages/Conferences/types/upsertConference.ts";
import { useCreateConference } from "./useCreateConference.ts";
import { useDeleteConference } from "./useDeleteConference.ts";
import { useUpdateConference } from "./useUpdateConference.ts";

export const useConferenceService = () => {
  const { mutateAsync: createConferenceFn, isPending: isCreatePending } = useCreateConference();
  const { mutateAsync: updateConferenceFn, isPending: isUpdatePending } = useUpdateConference();
  const { mutateAsync: deleteConferenceFn, isPending: isDeletePending } = useDeleteConference();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove } = useApiOperations(ApiOperationItems.CONFERENCE);

  const createConference = async (payload: CreateConference) => {
    const result = await create(() => createConferenceFn(payload), {
      isSucceeded: () => true,
    });

    if (result !== undefined) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/conferences`, { state: { search: payload.nameLocal } });
    }

    return result;
  };

  const updateConference = (conferenceId: string, payload: Omit<UpdateConference, "conferenceId">) =>
    update(() => updateConferenceFn({ ...payload, conferenceId }));

  const deleteConference = async (conferenceId: string) => Boolean(await remove(() => deleteConferenceFn(conferenceId)));

  return {
    createConference,
    isCreatePending,
    updateConference,
    isUpdatePending,
    deleteConference,
    isDeletePending,
  };
};
